import { HttpClient, PaginatedResponse, robotoHeaders } from "../../http";
import { MetadataChangeset } from "../metadataChangeset";

import { type DeviceTypeRecord } from "./DeviceTypeRecord";

export interface CreateDeviceTypeRequest {
  /** Unique within the org. */
  name: string;
  description?: string;
  metadata: Record<string, unknown>;
  tags: string[];
}

/** Fields left out change nothing. */
export interface UpdateDeviceTypeRequest {
  name?: string;
  /** `null` clears the description. */
  description?: string | null;
  metadata_changeset?: MetadataChangeset;
}

interface Options {
  abortSignal?: AbortSignal;
}

/**
 * The device types routes, `/v1/device-types`. Every route answers 403 for an
 * org without `release-asset-store`.
 */
export class DeviceTypeService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  /** Creates a device type in `options.orgId`, which the route reads from the caller-org header. */
  public async createDeviceType(
    request: CreateDeviceTypeRequest,
    options: Options & { orgId: string },
  ): Promise<DeviceTypeRecord> {
    const requestUrl = this.#httpClient.constructUrl("v1/device-types/create");
    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({
        orgId: options.orgId,
        resourceOwnerId: options.orgId,
      }),
      body: JSON.stringify(request),
      signal: options.abortSignal,
    });
    return response.json<DeviceTypeRecord>();
  }

  /** One page of an org's device types, newest first. */
  public async getDeviceTypesByOrg(
    orgId: string,
    options?: Options & { pageToken?: string },
  ): Promise<PaginatedResponse<DeviceTypeRecord>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }
    const requestUrl = this.#httpClient.constructUrl(
      `v1/device-types/org/${orgId}`,
      searchParams,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<DeviceTypeRecord>>();
  }

  /** Every device type in an org, reading page after page until the last. */
  public async getAllDeviceTypesByOrg(
    orgId: string,
    options?: Options,
  ): Promise<DeviceTypeRecord[]> {
    const all: DeviceTypeRecord[] = [];
    let pageToken: string | undefined = undefined;
    do {
      const page: PaginatedResponse<DeviceTypeRecord> =
        await this.getDeviceTypesByOrg(orgId, {
          abortSignal: options?.abortSignal,
          pageToken,
        });
      all.push(...page.items);
      pageToken = page.next_token ?? undefined;
    } while (pageToken !== undefined);
    return all;
  }

  public async getDeviceType(
    deviceTypeId: string,
    options: Options & { resourceOwnerId: string },
  ): Promise<DeviceTypeRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/device-types/id/${encodeURIComponent(deviceTypeId)}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });
    return response.json<DeviceTypeRecord>();
  }

  public async updateDeviceType(
    deviceTypeId: string,
    updates: UpdateDeviceTypeRequest,
    options: Options & { resourceOwnerId: string },
  ): Promise<DeviceTypeRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/device-types/id/${encodeURIComponent(deviceTypeId)}`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      body: JSON.stringify(updates),
      signal: options.abortSignal,
    });
    return response.json<DeviceTypeRecord>();
  }

  /**
   * Deletes a device type and, through a background job, every file it holds.
   * Rejects with `RobotoConflictException` while any device is of the type.
   */
  public async deleteDeviceType(
    deviceTypeId: string,
    options: Options & { resourceOwnerId: string },
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/device-types/id/${encodeURIComponent(deviceTypeId)}`,
    );
    const response = await this.#httpClient.delete(requestUrl, {
      idempotent: false,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      signal: options.abortSignal,
    });
    await response.throwIfError();
  }
}
